import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { CtaButton } from "./CtaButton";
import { Sprig, Mark } from "./Botanical";
import { HERO_IMAGE } from "../../constants/site";

const ease = [0.22, 1, 0.36, 1];

const MaskedLine = ({ children, delay }) => (
    <span className="-mb-[0.1em] block overflow-hidden pb-[0.1em]">
        <motion.span
            className="block"
            initial={{ y: "112%" }}
            animate={{ y: "0%" }}
            transition={{ duration: 1, delay, ease }}
        >
            {children}
        </motion.span>
    </span>
);

export const Hero = () => {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
    const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "10%"]);
    const chipY = useTransform(scrollYProgress, [0, 1], [0, -46]);

    return (
        <header ref={ref} className="relative overflow-hidden" data-testid="hero-section">
            <div aria-hidden className="pointer-events-none absolute -right-24 -top-32 h-[28rem] w-[28rem] rounded-full bg-blush blur-3xl" />
            <div aria-hidden className="pointer-events-none absolute -left-32 top-40 h-[24rem] w-[24rem] rounded-full bg-gold/10 blur-3xl" />
            <Sprig className="pointer-events-none absolute -left-4 bottom-10 hidden w-20 -rotate-12 text-burgundy/15 sm:block" />
            <Sprig className="pointer-events-none absolute right-4 top-24 hidden w-14 rotate-[30deg] text-gold/25 lg:block" />

            <div className="relative z-10 mx-auto flex max-w-6xl items-center justify-between px-5 pt-6 sm:px-8">
                <div className="flex items-center gap-2.5" data-testid="brand-mark">
                    <Mark className="h-8 w-8 rounded-lg" />
                    <span className="font-sans text-[0.62rem] font-bold uppercase tracking-[0.28em] text-plum">
                        The 5-Day Reset
                    </span>
                </div>
                <span className="hidden items-center rounded-full border border-gold/40 bg-white/60 px-4 py-1.5 font-sans text-[0.62rem] font-semibold uppercase tracking-[0.22em] text-burgundy sm:inline-flex">
                    Instant Digital PDF
                </span>
            </div>

            <div className="relative z-10 mx-auto grid max-w-6xl grid-cols-1 items-center gap-12 px-5 pb-16 pt-9 sm:px-8 lg:grid-cols-12 lg:gap-10 lg:pb-24 lg:pt-14">
                <div className="lg:col-span-6">
                    <motion.p
                        initial={{ opacity: 0, y: 14 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 0.08, ease }}
                        className="mb-5 inline-flex items-center gap-2 rounded-full bg-blush px-4 py-1.5 font-sans text-[0.62rem] font-bold uppercase tracking-[0.22em] text-burgundy"
                        data-testid="hero-guide-badge"
                    >
                        <span className="h-1.5 w-1.5 rounded-full bg-gold" />
                        The 5-Day Husband Reset Guide
                    </motion.p>

                    <h1 className="font-serif text-[2.7rem] font-medium leading-[1.04] tracking-[-0.01em] text-plum sm:text-6xl lg:text-[4.4rem]">
                        <MaskedLine delay={0.18}>WHY DOES HE</MaskedLine>
                        <MaskedLine delay={0.3}>
                            FEEL <em className="italic text-burgundy">SO FAR</em>
                        </MaskedLine>
                        <MaskedLine delay={0.42}>
                            <em className="italic text-burgundy">AWAY?</em>
                        </MaskedLine>
                    </h1>

                    <motion.p
                        initial={{ opacity: 0, y: 18 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.72, ease }}
                        className="mt-5 font-serif text-xl italic text-burgundy sm:text-2xl"
                        data-testid="hero-subheadline"
                    >
                        Even when he&rsquo;s right beside you?
                    </motion.p>

                    <motion.p
                        initial={{ opacity: 0, y: 18 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.86, ease }}
                        className="mt-4 max-w-md text-base leading-relaxed text-mutedtext sm:text-lg"
                        data-testid="hero-supporting-text"
                    >
                        The problem may not be your partner.{" "}
                        <strong className="font-semibold text-plum">It may be the pattern between you.</strong>
                    </motion.p>

                    <motion.div
                        initial={{ opacity: 0, y: 18 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 1, ease }}
                        className="mt-8 flex flex-wrap items-center gap-4"
                    >
                        <span
                            className="inline-flex items-center rounded-full bg-gradient-to-r from-goldlight to-gold px-5 py-2.5 font-sans text-[0.72rem] font-extrabold uppercase tracking-[0.18em] text-white shadow-lg shadow-gold/30"
                            data-testid="hero-price-badge"
                        >
                            Just ₹49
                        </span>
                        <CtaButton testId="hero-cta-button">Get the 5-Day Reset</CtaButton>
                    </motion.div>

                    <motion.p
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.8, delay: 1.15 }}
                        className="mt-4 font-sans text-[0.72rem] font-medium uppercase tracking-[0.18em] text-mutedtext"
                        data-testid="hero-microcopy"
                    >
                        Instant Digital PDF <span className="mx-1.5 text-gold">&bull;</span> Read on Your Phone
                    </motion.p>
                </div>

                <div className="lg:col-span-6">
                    <motion.div
                        initial={{ opacity: 0, y: 34, scale: 0.97 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        transition={{ duration: 1.1, delay: 0.5, ease }}
                        className="relative mx-auto w-full max-w-sm lg:max-w-md"
                        data-testid="hero-image-frame"
                    >
                        <div
                            aria-hidden
                            className="absolute -inset-3 translate-x-3 translate-y-3 rounded-b-[2rem] rounded-t-[11rem] border border-gold/50"
                        />
                        <div className="relative overflow-hidden rounded-b-[2rem] rounded-t-[11rem] shadow-2xl shadow-plum/25">
                            <motion.img
                                src={HERO_IMAGE}
                                alt="Indian married couple sitting close together on a sofa, both looking away thoughtfully"
                                style={{ y: imgY }}
                                className="aspect-[4/5] h-[112%] w-full scale-110 object-cover"
                                data-testid="hero-couple-image"
                            />
                            <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-plum/25 via-transparent to-transparent" />
                        </div>
                        <motion.div
                            style={{ y: chipY }}
                            className="absolute -bottom-5 left-1/2 flex -translate-x-1/2 items-center gap-2 whitespace-nowrap rounded-full border border-blushline bg-white/95 px-5 py-2.5 shadow-xl shadow-plum/10 backdrop-blur"
                            data-testid="hero-floating-chip"
                        >
                            <span className="h-1.5 w-1.5 rounded-full bg-gold" />
                            <span className="font-sans text-[0.65rem] font-bold uppercase tracking-[0.18em] text-plum">
                                5 days &bull; A few minutes a day
                            </span>
                        </motion.div>
                    </motion.div>
                </div>
            </div>
        </header>
    );
};

export default Hero;
